import {
  QAOperationDocumentTypes,
  QAOperationJson,
} from '@scale/shared/types/qaOperation/qaOperation';
import {
  attemptReviewInactiveStates,
  isStateMachineInReview,
} from '@scale/shared/types/qaOperation/stateMachineDefs/attemptReviewStateMachine';
import React from 'react';
import UserContext from 'react-scale/components/ui/UserContext';
import { findQAOperation } from 'react-scale/hooks/qaOperation/qaOperationApi';

interface IQAOperationContext {
  qaOperation?: QAOperationJson;
  userId: string;
  attemptResult?: any;
  setQAOperation: (operation?: QAOperationJson) => void;
  getOperationID: () => string;
  getAttempterID: () => string;
  getReviewerID: () => string;
  getCurrentState: () => string;
  isInReview: () => boolean;
  isActive: () => boolean;
  currentUserIsAttempter: () => boolean;
  currentUserIsReviewer: () => boolean;
  documentType: () => string;
}

const defaultQAOperationContext: IQAOperationContext = {
  userId: '',
  setQAOperation: () => {},
  getAttempterID: () => '',
  getReviewerID: () => '',
  getCurrentState: () => '',
  getOperationID: () => '',
  isInReview: () => false,
  isActive: () => false,
  currentUserIsAttempter: () => false,
  currentUserIsReviewer: () => false,
  documentType: () => '',
};

const QAOperationContext = React.createContext<IQAOperationContext>(defaultQAOperationContext);
export const useQAOperationContext = () => React.useContext(QAOperationContext);

export const QAOperationContextProvider: React.FC<{
  initialQAOperation?: QAOperationJson;
  attemptResult?: any;
  children?: React.ReactNode;
}> = ({ initialQAOperation, attemptResult, children }) => {
  const userCtx = React.useContext(UserContext);
  const userId = React.useMemo(() => (userCtx._id || userCtx.id) as string, [userCtx]);
  const [qaOperation, setQAOperation] = React.useState<QAOperationJson | undefined>(
    initialQAOperation,
  );

  // we only need the setter
  const setIntervalRef = React.useState<any>()[1];
  React.useEffect(() => setQAOperation(initialQAOperation), [initialQAOperation]);

  React.useEffect(() => {
    setIntervalRef((prev: NodeJS.Timeout) => {
      if (prev) clearInterval(prev);
      if (!qaOperation) return undefined;
      return setInterval(() => {
        findQAOperation(qaOperation._id)
          .then(operation => operation && setQAOperation({ ...operation }))
          .catch(null);
      }, 30000);
    });
  }, [qaOperation, setIntervalRef]);

  const qaOperationContext = React.useMemo<IQAOperationContext>(() => {
    const attempterId = qaOperation?.stateMachine.context?.attemptAssignedTo ?? '';
    const reviewerId = qaOperation?.stateMachine.context?.reviewAssignedTo ?? '';
    const currentState = (qaOperation?.stateMachine?.currentState as string) ?? '';
    return {
      qaOperation,
      userId,
      attemptResult,
      setQAOperation,
      getOperationID: () => qaOperation?._id ?? '',
      getAttempterID: () => attempterId,
      getReviewerID: () => reviewerId,
      getCurrentState: () => currentState,
      isInReview: () => isStateMachineInReview(currentState),
      isActive: () => !attemptReviewInactiveStates.includes(currentState as any),
      currentUserIsAttempter: () => !!userId && userId === attempterId,
      currentUserIsReviewer: () => !!userId && userId === reviewerId,
      documentType: () => (qaOperation && QAOperationDocumentTypes[qaOperation.type]) || '',
    };
  }, [userId, qaOperation, attemptResult]);

  return (
    <QAOperationContext.Provider value={qaOperationContext}>{children}</QAOperationContext.Provider>
  );
};

export function qaOperationHasEvent(eventName: string, qaOperation?: QAOperationJson): boolean {
  return qaOperation?.stateMachine?.currentEvents?.includes(eventName) ?? false;
}

export default QAOperationContext;
